import React, { useState, useEffect } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { supabase, TABLES } from '../../config/supabase'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import Card from '../ui/Card'
import Button from '../ui/Button'
import Switch from '../ui/Switch'
import Badge from '../ui/Badge'
import toast from 'react-hot-toast'

const { 
  FiZap, FiTrendingUp, FiClock, FiStar, FiUsers, FiCalendar, 
  FiSave, FiSettings, FiAward, FiTarget, FiSun, FiMoon 
} = FiIcons

const GamificationSettings = () => {
  const { family, setFamily } = useAuth()
  const [loading, setLoading] = useState(false)
  const [settings, setSettings] = useState({
    enhanced_points: {
      enabled: false,
      quality_bonus: {
        enabled: true,
        multiplier: 1.5
      },
      speed_bonus: {
        enabled: true,
        time_threshold_minutes: 30,
        bonus_points: 5
      },
      helper_bonus: {
        enabled: true,
        bonus_points: 10
      }
    },
    point_multipliers: {
      enabled: false,
      happy_hour: {
        enabled: true,
        multiplier: 1.5,
        start_time: '18:00',
        end_time: '20:00',
        days: [1, 2, 3, 4, 5] // Monday to Friday
      },
      weekend_warriors: {
        enabled: true,
        multiplier: 2.0,
        categories: ['outdoor', 'cleaning'],
        days: [0, 6] // Sunday and Saturday
      },
      challenge_week: {
        enabled: false,
        multiplier: 2.5,
        start_date: null,
        end_date: null,
        theme: 'Spring Cleaning'
      }
    },
    schedule: {
      daily_enabled: true,
      weekly_enabled: true,
      monthly_enabled: true
    }
  })

  useEffect(() => {
    if (family?.gamification_settings) {
      setSettings(family.gamification_settings)
    }
  }, [family])

  const updateSetting = (path, value) => {
    const pathArray = path.split('.')
    setSettings(prev => {
      const newSettings = JSON.parse(JSON.stringify(prev))
      let current = newSettings
      
      for (let i = 0; i < pathArray.length - 1; i++) {
        current = current[pathArray[i]]
      }
      
      current[pathArray[pathArray.length - 1]] = value
      return newSettings
    })
  }

  const saveSettings = async () => {
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from(TABLES.FAMILIES)
        .update({ gamification_settings: settings })
        .eq('id', family.id)
        .select()
        .single()

      if (error) {
        console.error('Settings update error:', error)
        toast.error('Failed to save gamification settings')
      } else {
        setFamily(data)
        toast.success('Gamification settings saved! 🎮')
      }
    } catch (error) {
      console.error('Unexpected error:', error)
      toast.error('An unexpected error occurred')
    } finally {
      setLoading(false)
    }
  }

  const getDayName = (dayIndex) => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
    return days[dayIndex]
  }

  const getCategoryName = (category) => {
    const categories = {
      'cleaning': 'Cleaning',
      'dishes': 'Dishes',
      'laundry': 'Laundry',
      'pets': 'Pet Care',
      'homework': 'Homework',
      'outdoor': 'Outdoor',
      'cooking': 'Cooking',
      'organization': 'Organization',
      'other': 'Other'
    }
    return categories[category] || category
  }

  const formatTime = (time) => {
    const [hours, minutes] = time.split(':')
    const hour = parseInt(hours)
    const ampm = hour >= 12 ? 'PM' : 'AM'
    const displayHour = hour % 12 || 12
    return `${displayHour}:${minutes} ${ampm}`
  }

  if (!family) {
    return (
      <Card>
        <div className="text-center py-12">
          <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading gamification settings...</p>
        </div>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <div className="flex items-center gap-3 mb-4">
          <SafeIcon icon={FiZap} className="text-primary-500 text-xl" />
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Gamification Settings</h2>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Enhance your family's chore experience with bonus points and multipliers
            </p>
          </div>
        </div>
      </Card>

      {/* Enhanced Points System */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <SafeIcon icon={FiTrendingUp} className="text-green-500 text-xl" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Enhanced Points System</h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                Reward exceptional work, speed, and helping others
              </p>
            </div>
          </div>
          <Switch
            checked={settings.enhanced_points.enabled}
            onChange={(checked) => updateSetting('enhanced_points.enabled', checked)}
          />
        </div>

        {settings.enhanced_points.enabled && (
          <div className="space-y-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            {/* Quality Bonus */}
            <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <SafeIcon icon={FiStar} className="text-green-600 dark:text-green-400" />
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-gray-100">Quality Bonus</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Extra points for exceptional work (parent approval)
                    </p>
                  </div>
                </div>
                <Switch
                  checked={settings.enhanced_points.quality_bonus.enabled}
                  onChange={(checked) => updateSetting('enhanced_points.quality_bonus.enabled', checked)}
                />
              </div>
              {settings.enhanced_points.quality_bonus.enabled && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 min-w-[100px]">
                      Multiplier:
                    </label>
                    <select
                      value={settings.enhanced_points.quality_bonus.multiplier}
                      onChange={(e) => updateSetting('enhanced_points.quality_bonus.multiplier', parseFloat(e.target.value))}
                      className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                    >
                      <option value={1.25}>1.25x (25% bonus)</option>
                      <option value={1.5}>1.5x (50% bonus)</option>
                      <option value={1.75}>1.75x (75% bonus)</option>
                      <option value={2.0}>2.0x (Double points)</option>
                    </select>
                  </div>
                  <p className="text-xs text-green-700 dark:text-green-300">
                    When parents rate a task 4-5 stars, children get bonus points
                  </p>
                </div>
              )}
            </div>

            {/* Speed Bonus */}
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <SafeIcon icon={FiClock} className="text-blue-600 dark:text-blue-400" />
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-gray-100">Speed Bonus</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Bonus points for completing tasks quickly
                    </p>
                  </div>
                </div>
                <Switch
                  checked={settings.enhanced_points.speed_bonus.enabled}
                  onChange={(checked) => updateSetting('enhanced_points.speed_bonus.enabled', checked)}
                />
              </div>
              {settings.enhanced_points.speed_bonus.enabled && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 min-w-[100px]">
                      Bonus Points:
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="20"
                      value={settings.enhanced_points.speed_bonus.bonus_points}
                      onChange={(e) => updateSetting('enhanced_points.speed_bonus.bonus_points', parseInt(e.target.value))}
                      className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 w-20"
                    />
                  </div>
                  <p className="text-xs text-blue-700 dark:text-blue-300">
                    Awarded when tasks are completed 20% faster than estimated time
                  </p>
                </div>
              )}
            </div>

            {/* Helper Bonus */}
            <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <SafeIcon icon={FiUsers} className="text-purple-600 dark:text-purple-400" />
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-gray-100">Helper Bonus</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Points for helping siblings with their tasks
                    </p>
                  </div>
                </div>
                <Switch
                  checked={settings.enhanced_points.helper_bonus.enabled}
                  onChange={(checked) => updateSetting('enhanced_points.helper_bonus.enabled', checked)}
                />
              </div>
              {settings.enhanced_points.helper_bonus.enabled && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 min-w-[100px]">
                      Bonus Points:
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="50"
                      value={settings.enhanced_points.helper_bonus.bonus_points}
                      onChange={(e) => updateSetting('enhanced_points.helper_bonus.bonus_points', parseInt(e.target.value))}
                      className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 w-20"
                    />
                  </div>
                  <p className="text-xs text-purple-700 dark:text-purple-300">
                    Encourages teamwork and helping family members
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </Card>

      {/* Point Multipliers */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <SafeIcon icon={FiTarget} className="text-orange-500 text-xl" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Point Multipliers</h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                Special time periods and events with bonus multipliers
              </p>
            </div>
          </div>
          <Switch
            checked={settings.point_multipliers.enabled}
            onChange={(checked) => updateSetting('point_multipliers.enabled', checked)}
          />
        </div>

        {settings.point_multipliers.enabled && (
          <div className="space-y-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            {/* Happy Hour */}
            <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <SafeIcon icon={FiSun} className="text-yellow-600 dark:text-yellow-400" />
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-gray-100">Happy Hour</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {settings.point_multipliers.happy_hour.multiplier}x points during designated family time
                    </p>
                  </div>
                </div>
                <Switch
                  checked={settings.point_multipliers.happy_hour.enabled}
                  onChange={(checked) => updateSetting('point_multipliers.happy_hour.enabled', checked)}
                />
              </div>
              {settings.point_multipliers.happy_hour.enabled && (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Start Time:</label>
                      <input
                        type="time"
                        value={settings.point_multipliers.happy_hour.start_time}
                        onChange={(e) => updateSetting('point_multipliers.happy_hour.start_time', e.target.value)}
                        className="w-full px-3 py-1 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">End Time:</label>
                      <input
                        type="time"
                        value={settings.point_multipliers.happy_hour.end_time}
                        onChange={(e) => updateSetting('point_multipliers.happy_hour.end_time', e.target.value)}
                        className="w-full px-3 py-1 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">Active Days:</label>
                    <div className="flex flex-wrap gap-2">
                      {[0, 1, 2, 3, 4, 5, 6].map(day => (
                        <button
                          key={day}
                          onClick={() => {
                            const currentDays = settings.point_multipliers.happy_hour.days
                            const newDays = currentDays.includes(day)
                              ? currentDays.filter(d => d !== day)
                              : [...currentDays, day]
                            updateSetting('point_multipliers.happy_hour.days', newDays)
                          }}
                          className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                            settings.point_multipliers.happy_hour.days.includes(day)
                              ? 'bg-yellow-500 text-white'
                              : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                          }`}
                        >
                          {getDayName(day).slice(0, 3)}
                        </button>
                      ))}
                    </div>
                  </div>
                  <p className="text-xs text-yellow-700 dark:text-yellow-300">
                    Currently: {formatTime(settings.point_multipliers.happy_hour.start_time)} - {formatTime(settings.point_multipliers.happy_hour.end_time)}
                  </p>
                </div>
              )}
            </div>

            {/* Weekend Warriors */}
            <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <SafeIcon icon={FiCalendar} className="text-green-600 dark:text-green-400" />
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-gray-100">Weekend Warriors</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {settings.point_multipliers.weekend_warriors.multiplier}x points for weekend outdoor tasks
                    </p>
                  </div>
                </div>
                <Switch
                  checked={settings.point_multipliers.weekend_warriors.enabled}
                  onChange={(checked) => updateSetting('point_multipliers.weekend_warriors.enabled', checked)}
                />
              </div>
              {settings.point_multipliers.weekend_warriors.enabled && (
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">Task Categories:</label>
                    <div className="flex flex-wrap gap-2">
                      {['outdoor', 'cleaning', 'organization', 'pets'].map(category => (
                        <button
                          key={category}
                          onClick={() => {
                            const currentCategories = settings.point_multipliers.weekend_warriors.categories
                            const newCategories = currentCategories.includes(category)
                              ? currentCategories.filter(c => c !== category)
                              : [...currentCategories, category]
                            updateSetting('point_multipliers.weekend_warriors.categories', newCategories)
                          }}
                          className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                            settings.point_multipliers.weekend_warriors.categories.includes(category)
                              ? 'bg-green-500 text-white'
                              : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                          }`}
                        >
                          {getCategoryName(category)}
                        </button>
                      ))}
                    </div>
                  </div>
                  <p className="text-xs text-green-700 dark:text-green-300">
                    Active on weekends (Saturday & Sunday)
                  </p>
                </div>
              )}
            </div>

            {/* Challenge Week */}
            <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <SafeIcon icon={FiAward} className="text-purple-600 dark:text-purple-400" />
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-gray-100">Challenge Week</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {settings.point_multipliers.challenge_week.multiplier}x points for special themed weeks
                    </p>
                  </div>
                </div>
                <Switch
                  checked={settings.point_multipliers.challenge_week.enabled}
                  onChange={(checked) => updateSetting('point_multipliers.challenge_week.enabled', checked)}
                />
              </div>
              {settings.point_multipliers.challenge_week.enabled && (
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">Theme:</label>
                    <input
                      type="text"
                      value={settings.point_multipliers.challenge_week.theme}
                      onChange={(e) => updateSetting('point_multipliers.challenge_week.theme', e.target.value)}
                      placeholder="e.g., Spring Cleaning, Holiday Prep"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Start Date:</label>
                      <input
                        type="date"
                        value={settings.point_multipliers.challenge_week.start_date || ''}
                        onChange={(e) => updateSetting('point_multipliers.challenge_week.start_date', e.target.value)}
                        className="w-full px-3 py-1 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">End Date:</label>
                      <input
                        type="date"
                        value={settings.point_multipliers.challenge_week.end_date || ''}
                        onChange={(e) => updateSetting('point_multipliers.challenge_week.end_date', e.target.value)}
                        className="w-full px-3 py-1 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                      />
                    </div>
                  </div>
                  {settings.point_multipliers.challenge_week.start_date && settings.point_multipliers.challenge_week.end_date && (
                    <div className="p-2 bg-purple-100 dark:bg-purple-800/30 rounded">
                      <p className="text-xs text-purple-700 dark:text-purple-300">
                        🎯 Active: {new Date(settings.point_multipliers.challenge_week.start_date).toLocaleDateString()} - {new Date(settings.point_multipliers.challenge_week.end_date).toLocaleDateString()}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </Card>

      {/* Schedule Controls */}
      <Card>
        <div className="flex items-center gap-3 mb-4">
          <SafeIcon icon={FiSettings} className="text-gray-500 text-xl" />
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Schedule Controls</h3>
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              Control when gamification features are active
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
            <div className="flex items-center gap-2">
              <SafeIcon icon={FiSun} className="text-yellow-500" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Daily Features</span>
            </div>
            <Switch
              checked={settings.schedule.daily_enabled}
              onChange={(checked) => updateSetting('schedule.daily_enabled', checked)}
            />
          </div>
          <div className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
            <div className="flex items-center gap-2">
              <SafeIcon icon={FiCalendar} className="text-blue-500" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Weekly Features</span>
            </div>
            <Switch
              checked={settings.schedule.weekly_enabled}
              onChange={(checked) => updateSetting('schedule.weekly_enabled', checked)}
            />
          </div>
          <div className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
            <div className="flex items-center gap-2">
              <SafeIcon icon={FiMoon} className="text-purple-500" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Monthly Features</span>
            </div>
            <Switch
              checked={settings.schedule.monthly_enabled}
              onChange={(checked) => updateSetting('schedule.monthly_enabled', checked)}
            />
          </div>
        </div>
      </Card>

      {/* Current Status */}
      {(settings.enhanced_points.enabled || settings.point_multipliers.enabled) && (
        <Card className="bg-blue-50 dark:bg-blue-900/20">
          <div className="flex items-center gap-3 mb-4">
            <SafeIcon icon={FiZap} className="text-blue-500 text-xl" />
            <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100">Current Status</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">Active Features:</h4>
              <div className="space-y-1">
                {settings.enhanced_points.enabled && (
                  <Badge variant="success" size="sm">Enhanced Points Active</Badge>
                )}
                {settings.point_multipliers.enabled && (
                  <Badge variant="info" size="sm">Point Multipliers Active</Badge>
                )}
                {!settings.enhanced_points.enabled && !settings.point_multipliers.enabled && (
                  <Badge variant="secondary" size="sm">Standard Points Only</Badge>
                )}
              </div>
            </div>
            <div>
              <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">Quick Actions:</h4>
              <div className="space-y-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    updateSetting('enhanced_points.enabled', true)
                    updateSetting('point_multipliers.enabled', true)
                  }}
                  className="w-full text-blue-600 border-blue-300 hover:bg-blue-50"
                >
                  Enable All Features
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    updateSetting('enhanced_points.enabled', false)
                    updateSetting('point_multipliers.enabled', false)
                  }}
                  className="w-full text-gray-600 border-gray-300 hover:bg-gray-50"
                >
                  Use Standard Points
                </Button>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          variant="primary"
          onClick={saveSettings}
          loading={loading}
          className="flex items-center gap-2"
        >
          <SafeIcon icon={FiSave} className="text-sm" />
          Save Gamification Settings
        </Button>
      </div>
    </div>
  )
}

export default GamificationSettings